// ============================================================
// UNDERWAY WATCH BILL - generation + verification.
//
// Two independent rotations while underway:
//   - watch LEADERS: 4h watches tiling the clock from startHour,
//     rotating through the pool (3 leaders => each has two 4h
//     watches/day, 8h work).
//   - LOOKOUTS: same 4h tiling, offset +2h, so a lookout changes
//     at the midpoint of a leader's watch - the bridge never
//     changes over all at once.
//   - everyone else: day work dayStart-dayEnd while underway.
//
// All times are SHIP-LOCAL wall time ('YYYY-MM-DDTHH:mm').
// Hours outside the passage are marked REST in this PLAN - what
// was actually worked before departure belongs to the HoR record,
// not here.
// ============================================================

const pad = n => String(n).padStart(2, '0')
const mod = (a, b) => ((a % b) + b) % b

function dayOf(iso) { return iso.slice(0, 10) }
function hourOf(iso) { return parseInt(iso.slice(11, 13), 10) || 0 }
function minOf(iso) { return parseInt(iso.slice(14, 16), 10) || 0 }
function addDays(day, n) {
  const d = new Date(day + 'T12:00:00')
  d.setDate(d.getDate() + n)
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
}
function dayDiff(a, b) {
  return Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000)
}

// generateWatchBill({ dep, arr, leaders, lookouts, others, startHour, watchLen, lookoutOffset, dayStart, dayEnd })
// leaders/lookouts: [{ name, role }] in rotation order. others: [{ name, role }].
export function generateWatchBill(opts) {
  const {
    dep, arr, leaders = [], lookouts = [], others = [],
    startHour = 6, watchLen = 4, lookoutOffset = 2, dayStart = 8, dayEnd = 17,
  } = opts
  const problems = []
  if (!dep || !arr) problems.push('Departure and arrival are required.')
  if (dep && arr && arr <= dep) problems.push('Arrival must be after departure.')
  if (!leaders.length) problems.push('No watch leaders picked.')
  if (!lookouts.length) problems.push('No lookouts picked.')
  const overlap = leaders.filter(l => lookouts.some(k => k.name === l.name)).map(l => l.name)
  if (overlap.length) problems.push('In both pools (pick one): ' + overlap.join(', '))
  if (problems.length) return { days: [], rows: [], problems }

  const depDay = dayOf(dep)
  const arrDay = dayOf(arr)
  const nDays = dayDiff(depDay, arrDay) + 1
  const days = Array.from({ length: nDays }, (_, i) => addDays(depDay, i))

  // absolute hour indices from midnight of the departure day;
  // departure floors, arrival ceils - coverage never undershoots.
  const depAbs = hourOf(dep)
  const arrAbs = dayDiff(depDay, arrDay) * 24 + hourOf(arr) + (minOf(arr) > 0 ? 1 : 0)

  const everyone = [
    ...leaders.map(c => ({ ...c, kind: 'leader' })),
    ...lookouts.map(c => ({ ...c, kind: 'lookout' })),
    ...others.map(c => ({ ...c, kind: 'day' })),
  ]
  const grid = {}   // name -> Array(nDays*24) of 'W'/'R'
  everyone.forEach(c => { grid[c.name] = new Array(nDays * 24).fill('R') })

  for (let H = depAbs; H < arrAbs; H++) {
    const leaderIdx = mod(Math.floor((H - startHour) / watchLen), leaders.length)
    const lookoutIdx = mod(Math.floor((H - startHour - lookoutOffset) / watchLen), lookouts.length)
    grid[leaders[leaderIdx].name][H] = 'W'
    grid[lookouts[lookoutIdx].name][H] = 'W'
    const hod = mod(H, 24)
    if (hod >= dayStart && hod < dayEnd) others.forEach(c => { grid[c.name][H] = 'W' })
  }

  const rows = everyone.map(c => {
    const cells = {}
    days.forEach((d, i) => { cells[d] = grid[c.name].slice(i * 24, i * 24 + 24).join('') })
    return { name: c.name, role: c.role || '', kind: c.kind, cells }
  })
  return { days, rows, problems: [], depAbs, arrAbs, meta: { startHour, watchLen, lookoutOffset, dayStart, dayEnd } }
}

// Verify: per-crew per-day structure via checkRow (circular - exact for a
// daily-repeating rotation), PLUS a rolling 10h-in-any-24h sweep across the
// WHOLE passage timeline, crossing midnights.
export function verifyWatchBill(bill) {
  const issues = []
  if (bill.problems?.length) return { ok: false, issues: bill.problems.map(p => ({ level: 'red', who: '-', day: '-', msg: p })) }

  // Structural check on the CONTINUOUS passage timeline, not isolated calendar
  // days - a 22:00-02:00 watch is one watch, and the rest around it is judged
  // as it actually lies. Rule (as in HoursOfRest evaluateDay): the day needs
  // >=10h rest, achievable in at most 2 qualifying periods (>=1h), one >=6h,
  // and <=14h between qualifying rests. Extra fragments are bonus, never a
  // violation - an arrival stub must not condemn a legal rotation.
  const lineRuns = (str, ch) => {
    const runs = []; let start = -1
    for (let i = 0; i <= str.length; i++) {
      if (i < str.length && str[i] === ch) { if (start < 0) start = i }
      else if (start >= 0) { runs.push({ start, len: i - start }); start = -1 }
    }
    return runs
  }
  for (const r of bill.rows) {
    const flat = bill.days.map(d => r.cells[d]).join('')
    bill.days.forEach((d, di) => {
      const s0 = di * 24
      const slice = flat.slice(s0, s0 + 24)
      const restHours = slice.split('').filter(c => c === 'R').length
      if (restHours < 10) { issues.push({ level: 'red', who: r.name, day: d, msg: 'Only ' + restHours + 'h rest in the day' }); return }
      // rest periods, extended into real neighbouring data at the day's edges
      const periods = lineRuns(slice, 'R').map(p => {
        let start = s0 + p.start, len = p.len
        if (p.start === 0) { let k = s0 - 1; while (k >= 0 && flat[k] === 'R') { start--; len++; k-- } }
        if (p.start + p.len === 24) { let k = s0 + 24; while (k < flat.length && flat[k] === 'R') { len++; k++ } }
        return { start, len }
      })
      // dedupe periods that merged into the same extended block
      const seen = new Set(); const uniq = []
      for (const p of periods) { const key = p.start + ':' + p.len; if (!seen.has(key)) { seen.add(key); uniq.push(p) } }
      const q = uniq.filter(p => p.len >= 1).map(p => p.len).sort((a, b) => b - a)
      const longest = q[0] || 0
      if (longest < 6) { issues.push({ level: 'red', who: r.name, day: d, msg: 'Longest rest period is ' + longest + 'h - one must be at least 6h' }); return }
      if (!(q[0] >= 10 || (q[0] + (q[1] || 0)) >= 10)) {
        issues.push({ level: 'red', who: r.name, day: d, msg: '10h rest cannot be met within 2 rest periods' }); return
      }
      // max 14h between consecutive qualifying rests on the timeline
      const work = lineRuns(flat, 'W')
      for (const w of work) {
        if (w.len > 14 && w.start < s0 + 24 && w.start + w.len > s0) {
          issues.push({ level: 'red', who: r.name, day: d, msg: w.len + 'h between rest periods - maximum is 14h' }); break
        }
      }
    })
    // rolling 10h across the full passage (flat already built above)
    for (let s = 0; s + 24 <= flat.length; s++) {
      let rest = 0
      for (let h = s; h < s + 24; h++) if (flat[h] === 'R') rest++
      if (rest < 10) {
        issues.push({ level: 'red', who: r.name, day: bill.days[Math.floor(s / 24)], msg: 'Only ' + rest + 'h rest in the 24h from ' + pad(mod(s, 24)) + ':00' })
        break   // one report per crew is enough
      }
    }
  }

  // coverage: every underway hour has exactly one leader and one lookout
  for (let H = bill.depAbs; H < bill.arrAbs; H++) {
    const di = Math.floor(H / 24), hod = mod(H, 24)
    const d = bill.days[di]
    const lead = bill.rows.filter(r => r.kind === 'leader' && r.cells[d][hod] === 'W').length
    const look = bill.rows.filter(r => r.kind === 'lookout' && r.cells[d][hod] === 'W').length
    if (lead !== 1) issues.push({ level: 'red', who: '-', day: d, msg: pad(hod) + ':00 has ' + lead + ' watch leaders on the bridge (want 1)' })
    if (look !== 1) issues.push({ level: 'red', who: '-', day: d, msg: pad(hod) + ':00 has ' + look + ' lookouts (want 1)' })
    if (lead !== 1 || look !== 1) break
  }

  const red = issues.filter(i => i.level === 'red').length
  return { ok: red === 0, issues, redCount: red }
}
